import React, { useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Stack, CircularProgress } from "@mui/material";

// Register required chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const ChartPointsPerGame = ({ games, selectedTeam, gameType, colorsTeam }) => {
  const [loading, setLoading] = useState(true);
  const [cumulativePointsFinal, setCumulativePoints] = useState(0);
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
    fixedPointsPerGame: [],
  });

  useEffect(() => {
    const processGameData = (games) => {
      const labels = [];
      const pointsPerGame = [];
      const fixedPointsPerGame = [];
      const backgroundColors = [];
      let cumulativePoints = 0;
      let cumulativePointsFixe = 0;

      let opponentName = "";
      let opponentScore = 0;
      let teamScore = 0;

      games
        .filter(
          (game) =>
            game &&
            game.gameType === gameType &&
            (game.gameState === "FINAL" || game.gameState === "OFF"),
        )
        .forEach((game) => {
          const { awayTeam, homeTeam } = game;
          const isHomeTeam = homeTeam.abbrev === selectedTeam.rawTricode;
          const isAwayTeam = awayTeam.abbrev === selectedTeam.rawTricode;

          let points = 0;
          let pointsFix = 1.16;

          if (
            isHomeTeam &&
            (game.gameState === "FINAL" || game.gameState === "OFF")
          ) {
            points =
              homeTeam.score > awayTeam.score
                ? 2
                : game.gameOutcome.lastPeriodType === "REG"
                  ? 0
                  : 1;
            opponentName = awayTeam.placeName.default;
            opponentScore = awayTeam.score;
            teamScore = homeTeam.score;
          } else if (
            isAwayTeam &&
            (game.gameState === "FINAL" || game.gameState === "OFF")
          ) {
            points =
              awayTeam.score > homeTeam.score
                ? 2
                : game.gameOutcome.lastPeriodType === "REG"
                  ? 0
                  : 1;
            opponentName = homeTeam.placeName.default;
            opponentScore = homeTeam.score;
            teamScore = awayTeam.score;
          }

          cumulativePoints += points;
          cumulativePointsFixe += pointsFix;

          pointsPerGame.push(cumulativePoints);
          fixedPointsPerGame.push(cumulativePointsFixe); // Example fixed multiplier
          setCumulativePoints(cumulativePoints);

          labels.push(
            `${selectedTeam.fullName}: ${teamScore}\n${opponentName}: ${opponentScore}\nDate : ${game.gameDate}  (${points} pts)`,
          );

          if (points === 2 && game.gameOutcome.lastPeriodType != "REG") {
            backgroundColors.push(`#ffdb1b`); // Green for 2 points
          } else if (points === 2) {
            backgroundColors.push(`#ffdb1b`); // Green for 2 points
          } else if (points === 1) {
            backgroundColors.push("#808080"); // Yellow for 1 point
          } else if (points === 0) {
            backgroundColors.push("#454545"); // Red for 0 points
          } else {
            backgroundColors.push("gray"); // Default color
          }
        });

      setChartData({
        labels,
        datasets: [
          {
            label: "Cumulative Points",
            data: pointsPerGame,
            backgroundColor: backgroundColors,
            barPercentage: 0.75,
            categoryPercentage: 1.75,
            Tooltip: { enabled: false },
          },
          {
            label: "Playoff Qualification",
            data: fixedPointsPerGame,
            backgroundColor: "rgba(255, 255, 255, 1)",
            barPercentage: 0.25,
            categoryPercentage: 0.5,
            Tooltip: { enabled: false }, // Disable tooltip for this dataset
          },
        ],
        options: {
          plugins: {
            legend: {
              display: true,
              position: "top",
              color: "#ffffff",
              labels: {
                generateLabels: function (chart) {
                  // Define custom labels and colors for the legend
                  return [
                    {
                      text: "Gold - 2 Points", // Custom label
                      fillStyle: `#ffdb1b`, // Custom color
                      hidden: false, // Visibility toggle
                      lineCap: "round",
                      lineDash: [],
                      lineDashOffset: 0,
                      lineJoin: "miter",
                      strokeStyle: `#ffdb1b`,
                      pointStyle: "rect", // Shape of the legend marker
                    },
                    {
                      text: "Bronze - 1 Point",
                      fillStyle: "#B87333",
                      hidden: false,
                      pointStyle: "rect",
                    },
                    {
                      text: "Gray - 0 Points",
                      fillStyle: "#585858",
                      hidden: false,
                      pointStyle: "rect",
                    },
                    {
                      text: "Default",
                      fillStyle: "gray",
                      hidden: false,
                      pointStyle: "rect",
                    },
                  ];
                },
              },
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  // Disable tooltip for 'Playoff Qualification' dataset
                  if (context.dataset.label === "Playoff Qualification") {
                    return ""; // Return an empty string to disable tooltip
                  }
                  return context.dataset.label + ": " + context.raw;
                },
              },
            },
          },
        },
      });
      setLoading(false);
    };

    processGameData(games);
  }, [games, gameType]);

  return (
    <div className="chartPointsPerGameWrapper">
      {loading ? (
        <CircularProgress />
      ) : (
        <Bar
          className="chartPointsPerGame"
          data={{
            labels: chartData.labels,
            datasets: chartData.datasets,
          }}
          options={{
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                //max: 130, // Adjust based on expected maximum points
                ticks: {
                  maxWidth: 100,
                },
              },
              x: {
                ticks: {
                  display: false, // Hide x-axis labels to reduce clutter
                },
                beginAtZero: true,
                max: 82, // Adjust based on expected maximum points
              },
            },
            responsive: true,
            plugins: {
              legend: {
                display: false,
                position: "top",
                labels: {
                  color: "#ffffff",
                  generateLabels: function (chart) {
                    // Define custom labels and colors for the legend
                    return [
                      {
                        text: "Wins", // Custom label
                        fillStyle: "#ffdb1b", // Custom color
                        hidden: false, // Visibility toggle
                        lineCap: "round",
                        lineDash: [],
                        lineDashOffset: 0,
                        lineJoin: "miter",
                        strokeStyle: "#FFD700",
                        pointStyle: "rect", // Shape of the legend marker
                        color: "#ffffff",
                      },
                      {
                        text: "Losses OT",
                        fillStyle: "#606060",
                        hidden: false,
                        pointStyle: "rect",
                        strokeStyle: "#606060",
                        color: "#ffffff",
                      },
                      {
                        text: "Losses",
                        fillStyle: "#454545",
                        hidden: false,
                        pointStyle: "rect",
                        strokeStyle: "#454545",
                        color: "#454545",
                      },
                      {
                        text: "Playoff",
                        fillStyle: "#ffffff",
                        hidden: false,
                        pointStyle: "line",
                        strokeStyle: "#ffffff",
                        color: "#ffffff",
                      },
                    ];
                  },
                },
                //onClick: () => {},
              },
            },
          }}
        />
      )}
    </div>
  );
};

export default ChartPointsPerGame;
